import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  isTokenExpired,
  refreshTokenSuccess,
  logout,
  selectAccessToken,
  selectRefreshToken,
  selectIsAuthenticated,
} from "../../store/authSlice";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const INACTIVITY_TIMEOUT_MS = 2.5 * 60 * 60 * 1000; // 2.5 hours (150 minutes)
const ACTIVITY_THROTTLE_MS = 5000; // 5-second event throttle
const LAST_ACTIVITY_KEY = "lastActivityTime";

/**
 * Hook to proactively manage auth tokens, silent token refresh, and 2.5-hour inactivity timeout.
 * - Active users: interactions (mouse, keyboard, scroll, click, touch, focus) continuously reset the 2.5-hour timer.
 *   Tokens are automatically and silently refreshed before expiry without interrupting the user.
 * - Inactive users: left idle for 2.5 continuous hours are logged out and redirected to /login.
 */
export const useAuthSession = () => {
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAccessToken);
  const refreshToken = useSelector(selectRefreshToken);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isRefreshingRef = useRef(false);
  const lastRecordedRef = useRef(0);

  const getLastActivityTime = useCallback(() => {
    try {
      const saved = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    } catch {}
    return Date.now();
  }, []);

  const handleInactivityLogout = useCallback(() => {
    console.warn("User inactive for 2.5 hours. Logging out.");
    dispatch(logout());
    try {
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    } catch {}
    toast.info("You have been logged out due to 2.5 hours of inactivity. Please log in again.");
  }, [dispatch]);

  const attemptRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    if (!refreshToken || isTokenExpired(refreshToken)) {
      console.warn("Refresh token expired or missing. Logging out.");
      dispatch(logout());
      toast.info("Your session has expired. Please log in again.");
      return;
    }

    try {
      isRefreshingRef.current = true;
      const { data } = await axios.post(
        `${API_URL}/token/refresh`,
        { refresh_token: refreshToken },
        { headers: { Authorization: `Bearer ${refreshToken}` } }
      );

      const newAccessToken = data?.access_token;
      if (newAccessToken) {
        dispatch(refreshTokenSuccess({ access_token: newAccessToken }));
      }
    } catch (err) {
      console.warn("Proactive token refresh encountered an error:", err);
      // Only log out if the backend explicitly rejected the token as invalid or expired (401 or 422)
      // Do NOT log out on network drops, temporary server errors (5xx), or offline states
      if (err.response?.status === 401 || err.response?.status === 422) {
        dispatch(logout());
        toast.info("Your session has expired. Please log in again.");
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshToken, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Initialize or refresh last activity timestamp on mount
    const currentStored = getLastActivityTime();
    const now = Date.now();
    if (!localStorage.getItem(LAST_ACTIVITY_KEY) || (now - currentStored >= INACTIVITY_TIMEOUT_MS)) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      lastRecordedRef.current = now;
    } else {
      lastRecordedRef.current = currentStored;
    }

    // Record user activity with 5s throttling
    const recordUserActivity = () => {
      const currentTime = Date.now();
      if (currentTime - lastRecordedRef.current >= ACTIVITY_THROTTLE_MS) {
        lastRecordedRef.current = currentTime;
        try {
          localStorage.setItem(LAST_ACTIVITY_KEY, String(currentTime));
        } catch {}
      }
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "pointerdown",
      "click",
      "focus",
      "wheel",
      "input",
    ];

    activityEvents.forEach((evt) => {
      window.addEventListener(evt, recordUserActivity, { passive: true });
    });

    const checkSessionAndActivity = () => {
      const currentTime = Date.now();
      const lastActivity = getLastActivityTime();

      // If user has been inactive for >= 2.5 hours, log out
      if (currentTime - lastActivity >= INACTIVITY_TIMEOUT_MS) {
        handleInactivityLogout();
        return;
      }

      // If user is active and access token is expiring within 60 seconds, refresh silently
      if (isTokenExpired(accessToken, 60)) {
        attemptRefresh();
      }
    };

    // Immediate check on mount/token change
    checkSessionAndActivity();

    // Periodic heartbeat check every 15 seconds
    const intervalId = setInterval(checkSessionAndActivity, 15000);

    // Also update activity and check session when browser tab regains focus or visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        recordUserActivity();
        checkSessionAndActivity();
      }
    };

    const handleFocus = () => {
      recordUserActivity();
      checkSessionAndActivity();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      activityEvents.forEach((evt) => {
        window.removeEventListener(evt, recordUserActivity);
      });
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [
    isAuthenticated,
    accessToken,
    attemptRefresh,
    getLastActivityTime,
    handleInactivityLogout,
  ]);
};
